import type { InternalAxiosRequestConfig } from 'axios';

import {
  asAxiosConfigWithExtensions,
  readCancelDuplicate,
  readRequestMetadata,
} from './types';

const inFlightControllers = new Map<string, AbortController>();

function stableSerialize(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Builds a deduplication key for in-flight axios requests.
 */
export function buildInFlightRequestKey(
  config: InternalAxiosRequestConfig,
): string {
  const method = (config.method ?? 'get').toUpperCase();
  const url = config.url ?? '';
  const params = stableSerialize(config.params);
  return `${method}:${url}:${params}`;
}

/**
 * Cancels any prior in-flight duplicate and attaches a fresh abort signal.
 */
export function applyDuplicateRequestCancellation<
  T extends InternalAxiosRequestConfig,
>(config: T): T {
  if (!readCancelDuplicate(config)) {
    return config;
  }

  const key = buildInFlightRequestKey(config);
  inFlightControllers.get(key)?.abort();

  const controller = new AbortController();
  inFlightControllers.set(key, controller);

  const extended = asAxiosConfigWithExtensions(config);
  const metadata = extended.metadata ?? {
    startTime: Date.now(),
    retryCount: 0,
  };

  return {
    ...config,
    signal: controller.signal,
    metadata: {
      ...metadata,
      inFlightKey: key,
    },
  } as T;
}

/**
 * Removes the in-flight entry after a request completes.
 */
export function releaseInFlightRequest(
  config: InternalAxiosRequestConfig | undefined,
): void {
  if (!config) {
    return;
  }

  const key = readRequestMetadata(config)?.inFlightKey;
  if (!key) {
    return;
  }

  const active = inFlightControllers.get(key);
  if (active && config.signal === active.signal) {
    inFlightControllers.delete(key);
  }
}

/**
 * Resets duplicate-request tracking (for tests).
 */
export function resetDuplicateRequestGuard(): void {
  for (const controller of inFlightControllers.values()) {
    controller.abort();
  }
  inFlightControllers.clear();
}
